/**
 * Lot Detail — Owner UI.
 *
 * Route: /food-pulse/owner/inventory/lots/:id
 * Shows lot header, current qty, ledger, condition inspections, and
 * one-tap promote (offer / surprise box / donation).
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Tag, Gift, Recycle, QrCode, CheckCircle2 } from "lucide-react";
import { http } from "@/lib/api";

const GREEN = "#0F3D2E";
const CREAM = "#FBF7EF";

const PROMOTE_PRESETS = [
  { kind: "offer",   label: "Mark down",      discount_pct: 30 },
  { kind: "surplus", label: "Surprise Box",   discount_pct: 50 },
  { kind: "surplus", label: "Offer donation", discount_pct: 100 },
];

export default function OwnerInventoryLotDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [promoteBusy, setPromoteBusy] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await http.get(`/inventory/lots/${id}`);
      setData(r.data);
    } catch (e) { setError(e.response?.data?.detail || "Load failed"); }
    setLoading(false);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const promote = async (preset) => {
    setPromoteBusy(preset.label);
    const endsAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    try {
      const r = await http.post(`/inventory/lots/${id}/promote`, {
        kind: preset.kind,
        discount_pct: preset.discount_pct,
        ends_at: endsAt,
        title: `${data.lot.product_name} — ${preset.label}`,
      });
      alert(`Deal live! /food-pulse/deals/${r.data.deal.slug}`);
      reload();
    } catch (e) { alert(e.response?.data?.detail || "Promote failed"); }
    setPromoteBusy(null);
  };

  const adjust = async (event, delta) => {
    const reason = window.prompt(`Reason for ${event} of ${Math.abs(delta)}?`, event);
    if (reason === null) return;
    try {
      await http.post(`/inventory/lots/${id}/adjust`, { event, delta_qty: delta, reason });
      reload();
    } catch (e) { alert(e.response?.data?.detail || "Adjust failed"); }
  };

  if (loading) return <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
  if (error) return <div className="p-10 text-center text-red-700" data-testid="lot-error">{error}</div>;
  if (!data) return null;

  const { lot, ledger, inspections } = data;
  const days = lot.days_to_expiry;

  return (
    <main className="min-h-screen" style={{ background: CREAM }} data-testid="owner-lot-detail">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to={`/food-pulse/owner/inventory?business_id=${lot.business_id}`} className="inline-flex items-center gap-1 text-sm font-semibold mb-4" style={{ color: GREEN }}>
          <ArrowLeft size={14} /> Back to inventory
        </Link>

        <div className="rounded-2xl bg-white border p-5 mb-5" style={{ borderColor: "#E9D8B4" }}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-xl font-black" style={{ color: GREEN }}>{lot.product_name}</h1>
              <div className="text-sm text-gray-600">GTIN: {lot.product_gtin || "—"} · Lot {lot.lot_no || lot.id.slice(0, 6)}</div>
            </div>
            {lot.qr_url && (
              <a href={lot.qr_url} target="_blank" rel="noreferrer" className="rounded-full border px-3 py-2 text-xs font-semibold inline-flex items-center gap-1" style={{ borderColor: GREEN, color: GREEN }} data-testid="qr-link">
                <QrCode size={14} /> Lot QR
              </a>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-center">
            <div className="rounded-xl p-3 border" style={{ borderColor: "#E9D8B4" }}>
              <div className="text-xs text-gray-600">Current qty</div>
              <div className="text-xl font-black" style={{ color: GREEN }} data-testid="current-qty">{lot.current_qty} {lot.unit}</div>
            </div>
            <div className="rounded-xl p-3 border" style={{ borderColor: "#E9D8B4" }}>
              <div className="text-xs text-gray-600">Days to expiry</div>
              <div className="text-xl font-black" style={{ color: days !== null && days <= 2 ? "#b91c1c" : GREEN }}>{days ?? "—"}</div>
            </div>
            <div className="rounded-xl p-3 border" style={{ borderColor: "#E9D8B4" }}>
              <div className="text-xs text-gray-600">Condition</div>
              <div className="text-xl font-black" style={{ color: GREEN }}>{lot.condition_score}</div>
            </div>
            <div className="rounded-xl p-3 border" style={{ borderColor: "#E9D8B4" }}>
              <div className="text-xs text-gray-600">Status</div>
              <div className="text-xl font-black" style={{ color: GREEN }}>{lot.status}</div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            {PROMOTE_PRESETS.map((p) => (
              <button key={p.label} disabled={promoteBusy === p.label || (lot.current_qty || 0) <= 0} onClick={() => promote(p)} className="rounded-full px-3 py-2 text-xs font-semibold border disabled:opacity-50" style={{ borderColor: GREEN, color: GREEN }} data-testid={`promote-${p.label.replace(/ /g, '-').toLowerCase()}`}>
                {promoteBusy === p.label ? "…" : <>{p.kind === "surplus" ? <Gift size={12} className="inline mr-1" /> : <Tag size={12} className="inline mr-1" />}{p.label} {p.discount_pct}%</>}
              </button>
            ))}
            <button onClick={() => { const n = Number(window.prompt("Quantity sold?")); if (n > 0) adjust("sold", -n); }} className="rounded-full px-3 py-2 text-xs font-semibold border" style={{ borderColor: GREEN, color: GREEN }} data-testid="record-sold-btn">Record sold</button>
            <button onClick={() => { const n = Number(window.prompt("Quantity spoiled?")); if (n > 0) adjust("spoiled", -n); }} className="rounded-full px-3 py-2 text-xs font-semibold border" style={{ borderColor: "#b91c1c", color: "#b91c1c" }} data-testid="record-spoiled-btn"><Recycle size={12} className="inline mr-1" />Log spoiled</button>
          </div>
        </div>

        <section className="mb-6" data-testid="ledger-section">
          <h2 className="text-lg font-bold mb-3" style={{ color: GREEN }}>Ledger</h2>
          <div className="rounded-2xl bg-white border overflow-hidden" style={{ borderColor: "#E9D8B4" }}>
            <table className="w-full text-sm">
              <thead style={{ background: "#F5E6C4" }}>
                <tr><th className="text-left p-2">Time</th><th className="text-left p-2">Event</th><th className="text-right p-2">Δ Qty</th><th className="text-right p-2">After</th><th className="text-left p-2">Reason</th></tr>
              </thead>
              <tbody>
                {ledger.map((r) => (
                  <tr key={r.id} className="border-t" style={{ borderColor: "#F5E6C4" }}>
                    <td className="p-2 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="p-2 font-semibold" style={{ color: GREEN }}>{r.event}</td>
                    <td className="p-2 text-right" style={{ color: r.delta_qty < 0 ? "#b91c1c" : GREEN }}>{r.delta_qty}</td>
                    <td className="p-2 text-right">{r.after ?? "—"}</td>
                    <td className="p-2 text-xs text-gray-600">{r.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {inspections.length > 0 && (
          <section data-testid="inspections-section">
            <h2 className="text-lg font-bold mb-3" style={{ color: GREEN }}>Inspections</h2>
            <div className="grid gap-2 md:grid-cols-2">
              {inspections.map((ins) => (
                <div key={ins.id} className="rounded-xl border p-3 bg-white text-sm" style={{ borderColor: "#E9D8B4" }}>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-600">{new Date(ins.created_at).toLocaleString()}</div>
                    {ins.freshness_delta_pct !== null && ins.freshness_delta_pct !== undefined && (
                      <span className="text-xs font-bold" style={{ color: ins.freshness_delta_pct > 50 ? "#b91c1c" : GREEN }}>Δ {ins.freshness_delta_pct}%</span>
                    )}
                  </div>
                  {ins.notes && <div className="mt-1">{ins.notes}</div>}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
